import { isNil } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { WeeklyCalendar } from '@/entities/contract'
import {
  DateRange,
  isNilOrEmptyString,
  isSameRange,
  oneYearLater,
  parseDateRange,
  today,
} from '@/shared/lib'
import {
  RangeDatePicker,
  Calendar,
  type RangeDatePickerProps,
} from '@/shared/ui'

export const getDefaultDateRange = (value?: string) => {
  const normalizedValue = value?.replaceAll('.', '-')

  return (
    parseDateRange({ dateRange: normalizedValue, format: 'yyyy-MM-dd' }) ?? {
      from: new Date(),
      to: undefined,
    }
  )
}

type Props = Omit<RangeDatePickerProps, 'render'>

export const PeriodPicker = ({
  value,
  onChange,
  ref: refProp,
  align = 'start',
  alignOffset = 0,
  sideOffset = 0,
  ...restProps
}: Props) => {
  const t = useTranslations('date_selection')

  const [dateRange, setDateRange] = useState<DateRange>(
    getDefaultDateRange(value),
  )

  const getStartDate = () => {
    return dateRange.from as Date
  }

  const getEndDate = () => {
    return dateRange.to
  }

  const handleCalendarSelect = (date?: Date) => {
    setDateRange({
      from: date ?? today,
      to: undefined,
    })
  }

  const handleClose = (dateRange?: DateRange) => {
    // 기간을 선택하지 않고 닫는 경우
    if (isNil(dateRange) || isNilOrEmptyString(dateRange?.to)) {
      const rangeFromValue = getDefaultDateRange(value)

      // 팝업이 닫힌 후 변경하기 위함
      setTimeout(() => {
        setDateRange(rangeFromValue)
      }, 200)
    }
  }

  useEffect(() => {
    const rangeFromValue = getDefaultDateRange(value)

    if (isSameRange(rangeFromValue, dateRange)) {
      return
    }

    setDateRange(rangeFromValue)
  }, [value])

  return (
    <RangeDatePicker
      {...restProps}
      value={value}
      onChange={onChange}
      onClose={handleClose}
      popoverClassName="p-0 flex h-[564px] z-popover!"
      align={align}
      alignOffset={alignOffset}
      sideOffset={sideOffset}
      openOnFocus
      ref={refProp}
      render={({ onSelect, onClose }) => {
        const handlePeriodSelect = (date: Date) => {
          if (date) {
            const range = {
              from: getStartDate(),
              to: date,
            }

            setDateRange(range)
            onSelect(range)
            onClose(range)
          }
        }

        return (
          <>
            <div className="w-100 border-r border-gray-100 p-6">
              <h3 className="typo-headline2 mb-6 font-semibold text-black">
                {t('move_in_date.title')}
              </h3>
              <Calendar
                mode="single"
                size="large"
                required={true}
                className="p-0 shadow-none"
                selected={getStartDate()}
                onSelect={handleCalendarSelect}
                today={today}
                disabled={{
                  before: today,
                  after: oneYearLater,
                }}
                startMonth={today}
                endMonth={oneYearLater}
              />
            </div>
            <WeeklyCalendar
              startDate={getStartDate()}
              endDate={getEndDate()}
              onSelect={handlePeriodSelect}
              onClose={onClose}
            />
          </>
        )
      }}
    />
  )
}
